import { Injectable } from '@angular/core';
import { SyncProfilerService } from './sync-profiler.service';

const LOG_PREFIX = '[ResourceProfiler]';

interface MetricEntry {
  name: string;
  durationMs: number;
  timestamp: string;
  meta?: Record<string, any>;
}

/**
 * Lightweight profiler for resource feature UI paths.
 *
 * Captures click-to-render timing, list init duration, search indexing,
 * and per-operation counters. Complements SyncProfilerService which
 * covers the sync/disk paths.
 *
 * Disabled by default — zero overhead in production.
 *
 * Enable from devtools console:
 *   ng.getComponent(document.querySelector('app-root'))  // or inject via Angular
 *   // OR: set window.__RESOURCE_PROFILER_ENABLED = true before navigating to resources
 *
 * Read results:
 *   console.log(localStorage.getItem('resource_profile_log'))
 */
@Injectable({
  providedIn: 'root'
})
export class ResourceProfiler {
  private _enabled: boolean | null = null;
  private readonly marks = new Map<string, number>();
  private metrics: MetricEntry[] = [];
  private readonly counters = new Map<string, number>();
  private sessionStart: number | null = null;

  constructor(private readonly syncProfiler: SyncProfilerService) {}

  setEnabled(isEnabled: boolean): void {
    this._enabled = isEnabled;
    if (isEnabled) {
      console.log(`${LOG_PREFIX} Enabled — metrics will be captured`);
    }
  }

  /**
   * Returns true if explicitly enabled, or falls back to SyncProfilerService's state.
   * Enabling the sync profiler automatically enables resource profiling too.
   */
  isEnabled(): boolean {
    if (this._enabled !== null) return this._enabled;
    return this.syncProfiler.isEnabled();
  }

  /**
   * Place a named timestamp mark. Call measure() later with the same name to record duration.
   */
  mark(name: string): void {
    if (!this.isEnabled()) return;
    this.marks.set(name, performance.now());
  }

  /**
   * Measure elapsed time from a prior mark. Logs immediately and stores for dump().
   */
  measure(name: string, fromMark: string, meta?: Record<string, any>): void {
    if (!this.isEnabled()) return;
    const start = this.marks.get(fromMark);
    if (start == null) return;
    const durationMs = Number.parseFloat((performance.now() - start).toFixed(2));
    const entry: MetricEntry = {
      name,
      durationMs,
      timestamp: new Date().toISOString(),
      meta,
    };
    this.metrics.push(entry);
    const metaStr = meta ? ` ${JSON.stringify(meta)}` : '';
    console.log(`${LOG_PREFIX} ${name}: ${durationMs}ms${metaStr}`);
    this.persistEntry(entry);
    this.marks.delete(fromMark);
  }

  /**
   * Wrap a sync function with timing.
   */
  timeSync<T>(name: string, fn: () => T, meta?: Record<string, any>): T {
    if (!this.isEnabled()) return fn();
    this.mark(name);
    const result = fn();
    this.measure(name, name, meta);
    return result;
  }

  /**
   * Wrap an async function with timing.
   */
  async timeAsync<T>(name: string, fn: () => Promise<T>, meta?: Record<string, any>): Promise<T> {
    if (!this.isEnabled()) return fn();
    this.mark(name);
    try {
      const result = await fn();
      this.measure(name, name, meta);
      return result;
    } catch (error) {
      this.measure(name, name, { ...meta, failed: true });
      throw error;
    }
  }

  /**
   * Increment a named counter. Use for tracking markForCheck calls, subscription counts, etc.
   */
  count(name: string, amount = 1): void {
    if (!this.isEnabled()) return;
    this.counters.set(name, (this.counters.get(name) || 0) + amount);
  }

  /**
   * Reset counters only (call between interactions to isolate per-click counts).
   */
  resetCounters(): void {
    this.counters.clear();
  }

  /**
   * Start a profiling session. Call before navigating to resources.
   */
  startSession(): void {
    if (!this.isEnabled()) return;
    this.metrics = [];
    this.counters.clear();
    this.marks.clear();
    this.sessionStart = performance.now();
    console.log(`${LOG_PREFIX} Session started`);
  }

  /**
   * End session, dump all metrics to console and localStorage.
   */
  endSession(): void {
    if (!this.isEnabled() || this.sessionStart == null) return;
    const totalMs = Math.round(performance.now() - this.sessionStart);
    console.log(`${LOG_PREFIX} Session ended (${totalMs}ms total)`);
    const report = this.buildReport(totalMs);
    console.log(report);
    this.persistReport(report);
    this.sessionStart = null;
  }

  /**
   * Dump current metrics without ending the session.
   * Call from devtools: inject ResourceProfiler, call dump().
   */
  dump(): string {
    const totalMs = this.sessionStart != null
      ? Math.round(performance.now() - this.sessionStart)
      : null;
    const report = this.buildReport(totalMs);
    console.log(report);
    return report;
  }

  /**
   * Full reset — clears all state.
   */
  reset(): void {
    this.metrics = [];
    this.counters.clear();
    this.marks.clear();
    this.sessionStart = null;
  }

  private buildReport(sessionDurationMs: number | null): string {
    const lines: string[] = [];
    const timestamp = new Date().toISOString();

    lines.push(`=== RESOURCE PROFILE ${timestamp} ===`);
    if (sessionDurationMs != null) {
      lines.push(`Session duration: ${sessionDurationMs}ms`);
    }
    lines.push('');

    // Timing metrics grouped by name
    lines.push('--- Timing ---');
    if (this.metrics.length === 0) {
      lines.push('  (no metrics captured)');
    } else {
      // Group by name, show all occurrences
      const grouped = new Map<string, MetricEntry[]>();
      for (const m of this.metrics) {
        const list = grouped.get(m.name) || [];
        list.push(m);
        grouped.set(m.name, list);
      }
      for (const [name, entries] of grouped) {
        if (entries.length === 1) {
          const e = entries[0];
          const metaStr = e.meta ? `  ${JSON.stringify(e.meta)}` : '';
          lines.push(`  ${name}: ${e.durationMs}ms${metaStr}`);
        } else {
          const durations = entries.map(e => e.durationMs);
          const avg = Math.round(durations.reduce((a, b) => a + b, 0) / durations.length);
          const min = Math.min(...durations);
          const max = Math.max(...durations);
          lines.push(`  ${name}: avg=${avg}ms  min=${min}ms  max=${max}ms  count=${entries.length}`);
        }
      }
    }
    lines.push('');

    // Counters
    lines.push('--- Counters ---');
    if (this.counters.size === 0) {
      lines.push('  (no counters)');
    } else {
      for (const [name, value] of this.counters) {
        lines.push(`  ${name}: ${value}`);
      }
    }
    lines.push('');

    return lines.join('\n');
  }

  private persistEntry(entry: MetricEntry): void {
    try {
      const key = 'resource_profile_log';
      const existing = localStorage.getItem(key) ?? '';
      const metaStr = entry.meta ? ` ${JSON.stringify(entry.meta)}` : '';
      const line = `${entry.timestamp} | ${entry.name}: ${entry.durationMs}ms${metaStr}\n`;
      localStorage.setItem(key, existing + line);
    } catch {
      // localStorage may be unavailable — ignore silently
    }
  }

  private persistReport(report: string): void {
    try {
      const key = 'resource_profile_log';
      const existing = localStorage.getItem(key) ?? '';
      localStorage.setItem(key, existing + report + '\n\n');
    } catch {
      // localStorage may be unavailable — ignore silently
    }
  }
}
